import { Add as AddIcon, InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { isSlaFixtureMode, previewSlaDefinition } from "api";
import { getTranslation } from "common";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { useTranslations, useUser } from "hooks";
import { SLA_FORMAT_RELATIVE, type SlaPreviewResponse } from "model/slaModel";
import {
  SLA_EDITOR_TEXT_KEYS,
  slaEditorTargetKey,
  type SlaEditorTarget,
} from "model/slaEditorModel";
import { SLA_START_TEXT_KEYS, createSlaPreviewSequencer } from "model/slaStartModel";
import {
  slaPreviewRequestFromDraft,
  slaSuggestionFromPreview,
  type SlaSuggestion,
} from "model/slaSuggestionModel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSlaModelDraftContext } from "./slaModelDraftContext";
import SlaSuggestions from "./SlaSuggestions";
import SlaTargetDraftCard from "./SlaTargetDraftCard";

/**
 * Configurazione SLA di **un** target (processo o attivita') dentro il pannello
 * proprieta' del designer. La bozza e' quella del modello
 * (`useSlaModelDraft`): qui ci sono solo la card dei campi, l'aggiunta/rimozione
 * e la stima.
 *
 * Stima e proposte (F2c, come nell'editor precedente): la stima arriva dal
 * Server sulla definizione in bozza (`POST .../preview`, formato relativo), le
 * risposte fuori ordine non sovrascrivono quella corrente, `estimable = false`
 * e l'errore della sorgente sono dichiarati e **non** bloccano il salvataggio.
 * La stima non riscrive mai la bozza: nessun `onApply`.
 */
interface SlaTargetFormProps {
  target: SlaEditorTarget;
}

const SlaTargetForm = ({ target }: SlaTargetFormProps) => {
  const translations = useTranslations();
  const user = useUser();
  const sla = useSlaModelDraftContext();
  const targetKey = slaEditorTargetKey(target);
  const draft = sla?.getDraft(target) ?? null;
  const idorganization = sla?.idorganization ?? null;
  const loadedRevision = sla?.loadedRevision ?? null;
  const timezoneId = sla?.timezoneId ?? null;

  const [preview, setPreview] = useState<SlaPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  /** Sorgente della stima non raggiungibile: stato dichiarato, non un errore. */
  const [previewFailed, setPreviewFailed] = useState(false);
  const previewSequencerRef = useRef(createSlaPreviewSequencer(0));
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    previewSequencerRef.current.setRevision(loadedRevision ?? 0);
  }, [loadedRevision]);

  const runPreview = useCallback(async () => {
    const current = draftRef.current;
    if (!current || !idorganization) {
      setPreview(null);
      setPreviewFailed(false);
      return;
    }
    const token = previewSequencerRef.current.begin();
    setPreviewLoading(true);
    try {
      const response = await previewSlaDefinition(
        idorganization,
        slaPreviewRequestFromDraft(current, new Date().toISOString(), SLA_FORMAT_RELATIVE),
      );
      previewSequencerRef.current.settle(token, setPreview, response);
      previewSequencerRef.current.settle(token, setPreviewFailed, false);
    } catch {
      previewSequencerRef.current.settle(token, setPreview, null);
      previewSequencerRef.current.settle(token, setPreviewFailed, true);
    } finally {
      if (previewSequencerRef.current.isCurrent(token)) setPreviewLoading(false);
    }
  }, [idorganization]);

  const hasDraft = Boolean(draft);
  useEffect(() => {
    if (!hasDraft) {
      setPreview(null);
      setPreviewFailed(false);
      return;
    }
    const timer = setTimeout(() => {
      runPreview();
    }, 200);
    return () => clearTimeout(timer);
  }, [
    hasDraft,
    runPreview,
    draft?.durationValue,
    draft?.durationUnit,
    draft?.riskRemainingPercent,
  ]);

  const suggestions = useMemo<SlaSuggestion[]>(
    () => (preview ? [slaSuggestionFromPreview(targetKey, preview, loadedRevision)] : []),
    [preview, targetKey, loadedRevision],
  );

  /** Istante -> etichetta nella lingua app **e** nella timezone dell'organizzazione. */
  const dateTimeLabel = useCallback(
    (iso: string): string => {
      if (!iso || !timezoneId) return "";
      try {
        return new Intl.DateTimeFormat(user?.i18n || navigator.language, {
          dateStyle: "short",
          timeStyle: "short",
          timeZone: timezoneId,
        }).format(new Date(iso));
      } catch {
        return "";
      }
    },
    [timezoneId, user?.i18n],
  );

  if (!sla?.available) return null;

  const idPrefix = draft?.targetDesignerId
    ? `sla-target-${draft.targetDesignerId}`
    : "sla-target-process";

  return (
    <Stack spacing={1.5} data-testid="sla-target-form" data-target={targetKey}>
      {isSlaFixtureMode() && (
        <Alert severity="info">
          {getTranslation(translations, SLA_EDITOR_TEXT_KEYS.fixtureNotice)}
        </Alert>
      )}

      {sla.conflict && (
        <Alert
          severity="warning"
          action={
            <FlowentiButton
              size="small"
              variant="outlined"
              disabled={sla.saving || sla.loading}
              onClick={sla.reload}
            >
              {getTranslation(translations, SLA_EDITOR_TEXT_KEYS.reload)}
            </FlowentiButton>
          }
        >
          {getTranslation(translations, SLA_EDITOR_TEXT_KEYS.conflict)}
        </Alert>
      )}

      {sla.warnings.length > 0 && (
        <Alert severity="info">
          <Stack spacing={0}>
            <Typography variant="subtitle2">
              {getTranslation(translations, SLA_EDITOR_TEXT_KEYS.warnings)}
            </Typography>
            {sla.warnings.map((warning) => (
              <Typography key={warning} variant="caption">
                {warning}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {sla.loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {!sla.loading && !draft && (
        <Box>
          <FlowentiButton
            id="sla-editor-add-target"
            size="small"
            variant="text"
            // Testo come le intestazioni del pannello (`text.primary`, bianco in
            // dark mode): il `secondary` di default e' quasi invisibile sul Paper scuro.
            sx={{ color: "text.primary", "&:hover": { backgroundColor: "action.hover" } }}
            startIcon={<AddIcon />}
            disabled={sla.saving}
            onClick={() => sla.addTarget(target)}
          >
            {getTranslation(translations, SLA_EDITOR_TEXT_KEYS.addTarget)}
          </FlowentiButton>
        </Box>
      )}

      {!sla.loading && draft && (
        <>
          <SlaTargetDraftCard
            draft={draft}
            name={target.name}
            ownerOptions={sla.ownerOptions}
            ownerOptionsLoading={sla.ownerOptionsLoading}
            users={sla.users}
            roles={sla.roles}
            settings={sla.settings}
            disabled={sla.saving}
            dense
            showRequiredErrors={sla.isValidationShown(draft)}
            idPrefix={idPrefix}
            onChange={(patch) => sla.updateDraft(target, patch)}
            onRemove={() => sla.removeTarget(target)}
          />

          <Box
            data-testid="sla-editor-estimate"
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
              p: 1.5,
              borderRadius: 2,
              bgcolor: "action.hover",
            }}
          >
            <InfoOutlinedIcon fontSize="small" color="info" sx={{ mt: 0.25 }} />
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              {/* Senza timezone di organizzazione la stima non si rende: si
                  dichiara il motivo invece di usare il fuso del browser. */}
              {timezoneId === null && (
                <Alert
                  severity="warning"
                  data-testid="sla-editor-estimate-timezone-unavailable"
                  sx={{ py: 0 }}
                >
                  {getTranslation(translations, SLA_START_TEXT_KEYS.timezoneUnavailable)}
                </Alert>
              )}
              <SlaSuggestions
                testIdPrefix="sla-editor-suggestions"
                suggestions={suggestions}
                modelRevision={loadedRevision}
                estimateRenderedByCaller={timezoneId === null}
                sourceError={previewFailed}
                loading={previewLoading}
                formatDateTime={dateTimeLabel}
              />
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  );
};

export default SlaTargetForm;
